import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import React, { useEffect, useRef } from 'react';
import { ctrlWheelZoom } from './mapZoom';

/** Centar Sombora. Odatle kreće mapa kad geokoder nije ponudio nijednu tačku. */
const FALLBACK: [number, number] = [45.774, 19.112];

/**
 * Pin se crta iz CSS-a, ne iz slike. Leafletov podrazumevani marker traži tri
 * PNG-a čije putanje bundler prepiše, pa marker nestane bez ijedne greške u
 * konzoli — poznata zamka koja se najlakše izbegne tako što se slike ne koriste.
 */
const PIN = L.divIcon({
  className: 'map__pin',
  html: '<span></span>',
  iconSize: [22, 22],
  iconAnchor: [11, 22]
});

interface Props {
  latitude: number | null;
  longitude: number | null;
  /** Zumira bliže kad je predlog tačan, dalje kad je samo ulica ili mesto. */
  tight: boolean;
  onPick: (latitude: number, longitude: number) => void;
}

/**
 * Mapa sa jednim pinom koji se prevlači ili premešta klikom.
 *
 * Namerno je tanak omotač oko Leafleta, bez react-leaflet sloja: ekran ima samo
 * jedan marker i jednu radnju, pa dodatna biblioteka ne bi rešila nijedan
 * problem koji ovde postoji.
 */
export default function AddressMap({ latitude, longitude, tight, onPick }: Props) {
  const container = useRef<HTMLDivElement | null>(null);
  const map = useRef<L.Map | null>(null);
  const marker = useRef<L.Marker | null>(null);
  // Poziva se iz Leafletovih rukovalaca koji se vezuju jednom, pa mora ići kroz
  // referencu — inače bi zauvek zvali prvu verziju funkcije.
  const pick = useRef(onPick);

  useEffect(() => {
    pick.current = onPick;
  }, [onPick]);

  useEffect(() => {
    if (!container.current || map.current) return;

    const instance = L.map(container.current, { scrollWheelZoom: false })
      .setView(FALLBACK, 13);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '© OpenStreetMap'
    }).addTo(instance);

    // Čioda se NE dodaje na mapu odmah. Dodaje je tek prolaz ispod, i samo kad
    // tačka stvarno postoji.
    const pin = L.marker(FALLBACK, { draggable: true, icon: PIN });

    pin.on('dragend', () => {
      const position = pin.getLatLng();
      pick.current(position.lat, position.lng);
    });

    instance.on('click', (event: L.LeafletMouseEvent) => {
      pin.setLatLng(event.latlng);
      pick.current(event.latlng.lat, event.latlng.lng);
    });

    map.current = instance;
    marker.current = pin;

    const odjavi = ctrlWheelZoom(instance, container.current);

    return () => {
      odjavi();
      instance.remove();
      map.current = null;
      marker.current = null;
    };
  }, []);

  useEffect(() => {
    if (!map.current || !marker.current) return;

    const hasPoint = latitude !== null && longitude !== null;
    const position: [number, number] = hasPoint ? [latitude!, longitude!] : FALLBACK;

    marker.current.setLatLng(position);

    // Bez tačke nema ni čiode.
    //
    // Ranije je stajala na rezervnoj tački — Somboru — i za adresu u Tavankutu
    // izgledala kao ponuđeni pin, iako je ispod mape pisalo da tačke nema.
    // Dovoljan je jedan slučajan potez da se potvrdi kuća u pogrešnom gradu.
    if (hasPoint) {
      marker.current.addTo(map.current);
    } else {
      marker.current.remove();
    }

    map.current.setView(position, hasPoint && tight ? 18 : hasPoint ? 14 : 10);
    // Kontejner često dobije konačnu širinu tek posle prvog crtanja.
    map.current.invalidateSize();
  }, [latitude, longitude, tight]);

  return <div className="map" ref={container} />;
}
